import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  PropertyReviewStatus,
  ReviewStatus,
  LeadStatus,
  BookingStatus,
} from '@pms/shared';
import {
  fetchPlatformOverview,
  fetchBookingsOverview,
  fetchPlatformBookings,
  fetchPlatformOperators,
  fetchAssignableProperties,
  createOperator,
  fetchPlatformUsers,
  createOperatorAdmin,
  updateOperator,
  fetchPlatformProperties,
  hidePlatformProperty,
  unhidePlatformProperty,
  fetchPlatformReviewQueue,
  fetchPlatformGuestReviews,
  approveGuestReview,
  rejectGuestReview,
  fetchPlatformPropertyDetail,
  createPlatformProperty,
  importPropertyPreview,
  approvePlatformProperty,
  requestPlatformPropertyChanges,
  fetchPlatformLeads,
  updatePlatformLeadStatus,
  approveLead,
  requestLeadChanges,
  rejectLead,
  updatePlatformPropertyImages,
  fetchFeatured,
  featureProperty,
  unfeatureProperty,
  reorderFeatured,
  setEditorsPick,
} from '@/services/api/platform.api';
import type {
  CreatePlatformPropertyRequest,
  CreateOperatorAdminRequest,
  UpdateOperatorRequest,
  CreateOperatorRequest,
} from '@/services/api/platform.api';
import {
  fetchPropertyContentFull,
  fetchAmenityCatalogue,
  updatePropertyCore,
  upsertProfile,
  setContacts,
  upsertKyc,
  setAmenities,
  createUnit,
  updateUnit,
  deleteUnit,
  createMedia,
  setMediaCategory,
  reorderMedia,
  setMediaCover,
  deleteMedia,
  publishProperty,
  unpublishProperty,
  syncToPms,
} from '@/services/api/property-content.api';
import type {
  UpdateCoreBody,
  UpsertProfileBody,
  UpsertKycBody,
  ContactInput,
  AmenitySelectionInput,
  CreateUnitBody,
  UpdateUnitBody,
  CreateMediaBody,
} from '@/services/api/property-content.api';

const PLATFORM_KEY = ['platform'] as const;

export const platformKeys = {
  overview: [...PLATFORM_KEY, 'overview'] as const,
  bookingsOverview: [...PLATFORM_KEY, 'bookings-overview'] as const,
  bookings: (params: {
    limit?: number;
    offset?: number;
    status?: BookingStatus;
    q?: string;
  }) => [...PLATFORM_KEY, 'bookings', params] as const,
  operators: [...PLATFORM_KEY, 'operators'] as const,
  assignableProperties: [...PLATFORM_KEY, 'assignable-properties'] as const,
  users: [...PLATFORM_KEY, 'users'] as const,
  properties: [...PLATFORM_KEY, 'properties'] as const,
  reviews: (status: PropertyReviewStatus) => [...PLATFORM_KEY, 'reviews', status] as const,
  property: (id: string) => [...PLATFORM_KEY, 'property', id] as const,
  propertyFull: (id: string) => [...PLATFORM_KEY, 'property', id, 'full'] as const,
  amenityCatalogue: [...PLATFORM_KEY, 'amenity-catalogue'] as const,
  leads: (status: LeadStatus | 'all') => [...PLATFORM_KEY, 'leads', status] as const,
  featured: [...PLATFORM_KEY, 'featured'] as const,
};

export function usePlatformOverview() {
  return useQuery({
    queryKey: platformKeys.overview,
    queryFn: fetchPlatformOverview,
    staleTime: 30_000,
  });
}

export function useBookingsOverview() {
  return useQuery({
    queryKey: platformKeys.bookingsOverview,
    queryFn: fetchBookingsOverview,
    staleTime: 30_000,
  });
}

/**
 * The cross-tenant individual bookings list (super-admin console). `keepPrevious`
 * keeps the current page on screen while the next page / a new filter loads.
 */
export function usePlatformBookings(params: {
  limit?: number;
  offset?: number;
  status?: BookingStatus;
  q?: string;
}) {
  return useQuery({
    queryKey: platformKeys.bookings(params),
    queryFn: () => fetchPlatformBookings(params),
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

export function usePlatformOperators() {
  return useQuery({
    queryKey: platformKeys.operators,
    queryFn: fetchPlatformOperators,
    staleTime: 30_000,
  });
}

/** Properties eligible for the "create group" picker (movable = no bookings). */
export function useAssignableProperties(enabled = true) {
  return useQuery({
    queryKey: platformKeys.assignableProperties,
    queryFn: fetchAssignableProperties,
    enabled,
    staleTime: 15_000,
  });
}

/** Create a new group (+ optionally assign data-free properties). */
export function useCreateOperator() {
  const invalidate = useInvalidatePlatform();
  return useMutation({
    mutationFn: (body: CreateOperatorRequest) => createOperator(body),
    onSuccess: () => invalidate(),
  });
}

/** Every platform team member (all tenants/roles) + operator + last-login. */
export function usePlatformUsers() {
  return useQuery({
    queryKey: platformKeys.users,
    queryFn: fetchPlatformUsers,
    staleTime: 30_000,
  });
}

/**
 * Create a tenant ADMIN login for an operator (returns the one-time temp
 * password). Invalidates the whole platform tree — including the team-members
 * list — so the new member appears without a manual refresh.
 */
export function useCreateOperatorAdmin() {
  const invalidate = useInvalidatePlatform();
  return useMutation({
    mutationFn: ({
      tenantId,
      body,
    }: {
      tenantId: string;
      body: CreateOperatorAdminRequest;
    }) => createOperatorAdmin(tenantId, body),
    onSuccess: () => invalidate(),
  });
}

/** Rename a group / change its login code; refresh the operators list on success. */
export function useUpdateOperator() {
  const invalidate = useInvalidatePlatform();
  return useMutation({
    mutationFn: ({
      tenantId,
      body,
    }: {
      tenantId: string;
      body: UpdateOperatorRequest;
    }) => updateOperator(tenantId, body),
    onSuccess: () => invalidate(),
  });
}

export function usePlatformProperties(showHidden = false) {
  return useQuery({
    queryKey: [...platformKeys.properties, { showHidden }] as const,
    queryFn: () => fetchPlatformProperties(showHidden),
    staleTime: 30_000,
  });
}

export function usePlatformReviewQueue(status: PropertyReviewStatus) {
  return useQuery({
    queryKey: platformKeys.reviews(status),
    queryFn: () => fetchPlatformReviewQueue(status),
    staleTime: 15_000,
  });
}

/** Cross-tenant guest-review moderation queue (platform-scoped). */
export function usePlatformGuestReviews(status?: ReviewStatus) {
  return useQuery({
    queryKey: ['platform', 'guest-reviews', status ?? 'all'],
    queryFn: () => fetchPlatformGuestReviews(status),
    staleTime: 15_000,
  });
}

/**
 * Approve a post-stay guest review (→ APPROVED, shows on the storefront).
 * Invalidates the whole platform tree so the row leaves the Pending/Hidden tab
 * and the overview counts refresh.
 */
export function useApproveGuestReview() {
  const invalidate = useInvalidatePlatform();
  return useMutation({
    mutationFn: (id: string) => approveGuestReview(id),
    onSuccess: () => invalidate(),
  });
}

/** Reject/hide a post-stay guest review (→ HIDDEN, never shows publicly). */
export function useRejectGuestReview() {
  const invalidate = useInvalidatePlatform();
  return useMutation({
    mutationFn: (id: string) => rejectGuestReview(id),
    onSuccess: () => invalidate(),
  });
}

export function usePlatformPropertyDetail(id: string | null) {
  return useQuery({
    queryKey: platformKeys.property(id ?? ''),
    queryFn: () => fetchPlatformPropertyDetail(id as string),
    enabled: !!id,
  });
}

/** Invalidate every platform list so overview counts + queues refresh after a review action. */
function useInvalidatePlatform() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: PLATFORM_KEY });
  };
}

export function useCreatePlatformProperty() {
  const invalidate = useInvalidatePlatform();
  return useMutation({
    mutationFn: (body: CreatePlatformPropertyRequest) =>
      createPlatformProperty(body),
    onSuccess: () => invalidate(),
  });
}

/**
 * Parse an uploaded onboarding .xlsx into an "Add Property" prefill. Preview
 * only — creates nothing, so there is nothing to invalidate.
 */
export function useImportPropertyPreview() {
  return useMutation({
    mutationFn: (file: File) => importPropertyPreview(file),
  });
}

export function useApproveProperty() {
  const invalidate = useInvalidatePlatform();
  return useMutation({
    mutationFn: (id: string) => approvePlatformProperty(id),
    onSuccess: () => invalidate(),
  });
}

export function useRequestPropertyChanges() {
  const invalidate = useInvalidatePlatform();
  return useMutation({
    mutationFn: ({ id, feedback }: { id: string; feedback: string }) =>
      requestPlatformPropertyChanges(id, feedback),
    onSuccess: () => invalidate(),
  });
}

// ── Hide / unhide (TASK-113) ───────────────────────────────────────────────────

export function useHideProperty() {
  const invalidate = useInvalidatePropertyContent();
  return useMutation({
    mutationFn: (id: string) => hidePlatformProperty(id),
    onSuccess: (_data, id) => invalidate(id),
  });
}

export function useUnhideProperty() {
  const invalidate = useInvalidatePropertyContent();
  return useMutation({
    mutationFn: (id: string) => unhidePlatformProperty(id),
    onSuccess: (_data, id) => invalidate(id),
  });
}

// ── Leads (TASK-59) ───────────────────────────────────────────────────────────

export function usePlatformLeads(status: LeadStatus | 'all') {
  return useQuery({
    queryKey: platformKeys.leads(status),
    queryFn: () =>
      fetchPlatformLeads({
        ...(status !== 'all' ? { status } : {}),
        pageSize: 100,
      }),
    staleTime: 15_000,
  });
}

export function useUpdateLeadStatus() {
  const invalidate = useInvalidatePlatform();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: LeadStatus }) =>
      updatePlatformLeadStatus(id, status),
    onSuccess: () => invalidate(),
  });
}

// ── Lead decision loop (TASK-51) ────────────────────────────────────────────────
//
// Mirror useApproveProperty / useRequestPropertyChanges: each invalidates the
// whole ['platform'] tree so the leads queue moves the row to its new status tab
// and the overview counts refresh.

export function useApproveLead() {
  const invalidate = useInvalidatePlatform();
  return useMutation({
    mutationFn: (id: string) => approveLead(id),
    onSuccess: () => invalidate(),
  });
}

export function useRequestLeadChanges() {
  const invalidate = useInvalidatePlatform();
  return useMutation({
    mutationFn: ({ id, feedback }: { id: string; feedback: string }) =>
      requestLeadChanges(id, feedback),
    onSuccess: () => invalidate(),
  });
}

export function useRejectLead() {
  const invalidate = useInvalidatePlatform();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      rejectLead(id, reason),
    onSuccess: () => invalidate(),
  });
}

// ── Featured curation (TASK-61) ─────────────────────────────────────────────────
//
// One GET hydrates both columns (featured set + eligible pool). Mutations narrowly
// invalidate the featured key — the storefront feed is a separate React-Query
// instance in another app and picks up changes on its own staleTime.

export function useFeatured() {
  return useQuery({
    queryKey: platformKeys.featured,
    queryFn: fetchFeatured,
    staleTime: 15_000,
  });
}

function useInvalidateFeatured() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: platformKeys.featured });
  };
}

export function useSetFeatured() {
  const invalidate = useInvalidateFeatured();
  return useMutation({
    mutationFn: (id: string) => featureProperty(id),
    onSuccess: () => invalidate(),
  });
}

export function useUnfeature() {
  const invalidate = useInvalidateFeatured();
  return useMutation({
    mutationFn: (id: string) => unfeatureProperty(id),
    onSuccess: () => invalidate(),
  });
}

export function useReorderFeatured() {
  const invalidate = useInvalidateFeatured();
  return useMutation({
    mutationFn: (orderedIds: string[]) => reorderFeatured(orderedIds),
    onSuccess: () => invalidate(),
  });
}

export function useEditorsPick() {
  const invalidate = useInvalidateFeatured();
  return useMutation({
    mutationFn: (id: string) => setEditorsPick(id),
    onSuccess: () => invalidate(),
  });
}

// ── Property images (TASK-60) ──────────────────────────────────────────────────

export function useUpdatePropertyImages() {
  const invalidate = useInvalidatePlatform();
  return useMutation({
    mutationFn: ({ id, images }: { id: string; images: string[] }) =>
      updatePlatformPropertyImages(id, images),
    onSuccess: () => invalidate(),
  });
}

// ── Property content editor (TASK-100 / TASK-112) ──────────────────────────────
//
// Consumes the property-content controller (/admin/platform/properties/:id/full
// + the granular core/profile/contacts/kyc/amenities/units/media/publish/sync
// routes). Each mutation narrowly invalidates the property's `full` aggregate
// (so the editor reflects edits immediately) and the property list (so the
// Active/Live badges stay truthful) — never the whole platform tree, which would
// refetch overview/operators/leads needlessly.

/** Narrow invalidation: this property's full aggregate + the property list. */
function useInvalidatePropertyContent() {
  const queryClient = useQueryClient();
  return (id: string) => {
    void queryClient.invalidateQueries({ queryKey: platformKeys.propertyFull(id) });
    void queryClient.invalidateQueries({ queryKey: platformKeys.properties });
  };
}

export function usePropertyContentFull(id: string | null) {
  return useQuery({
    queryKey: platformKeys.propertyFull(id ?? ''),
    queryFn: () => fetchPropertyContentFull(id as string),
    enabled: !!id,
  });
}

export function useAmenityCatalogue() {
  return useQuery({
    queryKey: platformKeys.amenityCatalogue,
    queryFn: fetchAmenityCatalogue,
    staleTime: 5 * 60_000,
  });
}

export function useUpdatePropertyCore(id: string) {
  const invalidate = useInvalidatePropertyContent();
  return useMutation({
    mutationFn: (body: UpdateCoreBody) => updatePropertyCore(id, body),
    onSuccess: () => invalidate(id),
  });
}

export function useUpsertProfile(id: string) {
  const invalidate = useInvalidatePropertyContent();
  return useMutation({
    mutationFn: (body: UpsertProfileBody) => upsertProfile(id, body),
    onSuccess: () => invalidate(id),
  });
}

export function useSetContacts(id: string) {
  const invalidate = useInvalidatePropertyContent();
  return useMutation({
    mutationFn: (contacts: ContactInput[]) => setContacts(id, contacts),
    onSuccess: () => invalidate(id),
  });
}

export function useUpsertKyc(id: string) {
  const invalidate = useInvalidatePropertyContent();
  return useMutation({
    mutationFn: (body: UpsertKycBody) => upsertKyc(id, body),
    onSuccess: () => invalidate(id),
  });
}

export function useSetAmenities(id: string) {
  const invalidate = useInvalidatePropertyContent();
  return useMutation({
    mutationFn: (selections: AmenitySelectionInput[]) => setAmenities(id, selections),
    onSuccess: () => invalidate(id),
  });
}

export function useCreateUnit(id: string) {
  const invalidate = useInvalidatePropertyContent();
  return useMutation({
    mutationFn: (body: CreateUnitBody) => createUnit(id, body),
    onSuccess: () => invalidate(id),
  });
}

export function useUpdateUnit(id: string) {
  const invalidate = useInvalidatePropertyContent();
  return useMutation({
    mutationFn: ({ unitId, body }: { unitId: string; body: UpdateUnitBody }) =>
      updateUnit(id, unitId, body),
    onSuccess: () => invalidate(id),
  });
}

export function useDeleteUnit(id: string) {
  const invalidate = useInvalidatePropertyContent();
  return useMutation({
    mutationFn: (unitId: string) => deleteUnit(id, unitId),
    onSuccess: () => invalidate(id),
  });
}

export function useCreateMedia(id: string) {
  const invalidate = useInvalidatePropertyContent();
  return useMutation({
    mutationFn: (body: CreateMediaBody) => createMedia(id, body),
    onSuccess: () => invalidate(id),
  });
}

export function useSetMediaCategory(id: string) {
  const invalidate = useInvalidatePropertyContent();
  return useMutation({
    mutationFn: ({ mediaId, category }: { mediaId: string; category: string | null }) =>
      setMediaCategory(id, mediaId, category),
    onSuccess: () => invalidate(id),
  });
}

export function useReorderMedia(id: string) {
  const invalidate = useInvalidatePropertyContent();
  return useMutation({
    mutationFn: (orderedIds: string[]) => reorderMedia(id, orderedIds),
    onSuccess: () => invalidate(id),
  });
}

export function useSetMediaCover(id: string) {
  const invalidate = useInvalidatePropertyContent();
  return useMutation({
    mutationFn: (mediaId: string) => setMediaCover(id, mediaId),
    onSuccess: () => invalidate(id),
  });
}

export function useDeleteMedia(id: string) {
  const invalidate = useInvalidatePropertyContent();
  return useMutation({
    mutationFn: (mediaId: string) => deleteMedia(id, mediaId),
    onSuccess: () => invalidate(id),
  });
}

export function usePublishProperty(id: string) {
  const invalidate = useInvalidatePropertyContent();
  return useMutation({
    mutationFn: () => publishProperty(id),
    onSuccess: () => invalidate(id),
  });
}

export function useUnpublishProperty(id: string) {
  const invalidate = useInvalidatePropertyContent();
  return useMutation({
    mutationFn: () => unpublishProperty(id),
    onSuccess: () => invalidate(id),
  });
}

export function useSyncToPms(id: string) {
  const invalidate = useInvalidatePropertyContent();
  return useMutation({
    mutationFn: (confirm: string) => syncToPms(id, confirm),
    onSuccess: () => invalidate(id),
  });
}
